import {Component, computed, input} from '@angular/core';
import {required, validate} from '@angular/forms/signals';
import moment from 'moment';
import {StoreWorkingTime} from '../../../../classes/models/store/StoreWorkingTime';
import {DayOfWeek} from "../../../../classes/enums/DayOfWeek";
import {SHARED_IMPORTS} from "../../../../shared/standalone-imports";

/** Model for the common fields shared between ongoing and temporary preferred time off forms. */
export interface TimeOffCommonFieldsModel {
  allDay: boolean;
  startTime: string;
  endTime: string;
  reason: string;
}

/** Minimum possible start time. It should be used if a preferred time off happens all day. */
export const MIN_START_TIME = '00:00:00';
/** Maximum possible end time. It should be used if a preferred time off happens all day. */
export const MAX_END_TIME = '23:59:59';
/** A regular expression pattern for a valid start time and end time in 15-minute increments. */
export const TIME_REGEX = /^(0\d|1\d|2[0-3]):(00|15|30|45)(:00)?$/;

/**
 * Applies common field validation rules to the provided schema path.
 * This function is reusable across parent forms that include common time off fields.
 * @param path the schema path pointing to the common fields subtree.
 */
export function applyCommonFieldsValidation(path: any): void {
  required(path.reason);
  required(path.startTime);
  required(path.endTime);

  // Pattern validation for start time, only when not all day
  validate(path.startTime, ({value, valueOf}) => {
    if (!valueOf(path.allDay) && !TIME_REGEX.test(value() as string)) {
      return {kind: 'pattern', message: 'Start time must be in 15 minutes increments'};
    }
    return undefined;
  });

  // Pattern validation for end time, only when not all day
  validate(path.endTime, ({value, valueOf}) => {
    if (!valueOf(path.allDay) && !TIME_REGEX.test(value() as string)) {
      return {kind: 'pattern', message: 'End time must be in 15 minutes increments'};
    }
    return undefined;
  });

  // Cross-field validation: start time must be earlier than end time
  validate(path.endTime, ({value, valueOf}) => {
    const startTime = moment(valueOf(path.startTime), 'HH:mm:ss');
    const endTime = moment(value(), 'HH:mm:ss');
    if (startTime.isSameOrAfter(endTime)) {
      return {kind: 'startTimeIsAfterEndTime', message: 'Start time must be earlier than end time'};
    }
    return undefined;
  });
}

/** Full form model including the startDate and dayOfWeek used by parent ongoing time off form. */
export interface OngoingTimeOffFormModel {
  startDate: string;
  dayOfWeek: DayOfWeek | null;
  commonFields: TimeOffCommonFieldsModel;
}

/**
 * This component handles form fields that apply to both ongoing and temporary preferred time off forms.
 * It receives a signal form FieldTree slice from the parent and binds its template controls to individual fields.
 */
@Component({
  selector: 'app-time-off-form-common-fields',
  templateUrl: './time-off-form-common-fields.component.html',
  styleUrls: ['./time-off-form-common-fields.component.less'],
  imports: [SHARED_IMPORTS]
})
export class TimeOffFormCommonFieldsComponent {

  /** The FieldTree slice for the common fields, received from the parent signal form. */
  readonly fields = input.required<any>();
  /** Store earliest and latest working time for a week. */
  readonly storeWorkingTime = input.required<StoreWorkingTime>();

  /** Computed signal indicating whether the preferred time off happens all day. */
  readonly isAllDay = computed(() => this.fields().allDay().value());

  /**
   * Sets form fields of start time and end time to be times of the start of the day and the end of the day.
   */
  setToAllDay(): void {
    const fields = this.fields();
    fields.allDay.set(true);
    fields.startTime.set(MIN_START_TIME);
    fields.endTime.set(MAX_END_TIME);
  }

  /**
   * Sets form fields of start time and end time to be times of the earliest store opening and latest store closing.
   */
  setDefaultTimes(): void {
    const fields = this.fields();
    fields.allDay.set(false);
    fields.startTime.set(this.storeWorkingTime().openingTime);
    fields.endTime.set(this.storeWorkingTime().closingTime);
  }
}